import React, { FC, SyntheticEvent, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import * as ResponseQuery from 'graphql/response.graphql';
import usePermittedSessionPreferences from 'hooks/usePermittedSessionPreferences';
import ICONS from '../Icons';
import PaymentError from '../Error/PaymentError';
import { Response } from 'api/data/response/types';
import {
  OnSubmitFunction,
  PaymentErrorProps,
  PaymentErrorType,
  PaymentMethodSource,
  PaymentMethodType,
  Terminal,
} from 'api/data/payment/types';
import { Transaction } from '~/api/data/transaction/types';
import './style.scss';

type TerminalErrorType = {
  title?: string;
  reason: string;
  icon?: string;
  type?: string;
} | null;

interface TerminalPaymentProps {
  onSubmit: OnSubmitFunction;
  pageId?: string;
  response: Response;
  paymentError?: PaymentErrorProps;
  setLoading: (loading: boolean) => void;
  loading: boolean;
  spaceSlug?: string;
}

const POLLING_INTERVAL = 1000;

const TerminalPayment: FC<TerminalPaymentProps> = ({
  onSubmit,
  pageId,
  response,
  paymentError,
  setLoading,
  loading,
  spaceSlug,
}) => {
  const { canAcceptInPersonPayments, connectedPaymentTerminal } = usePermittedSessionPreferences(spaceSlug || '');
  const [errorMessage, setErrorMessage] = useState<TerminalErrorType>(null);

  const { startPolling, stopPolling } = useQuery<{ responseWithFirstPaymentTransaction: Response }>(
    ResponseQuery.GetResponseWithFirstPaymentTransaction,
    {
      variables: { id: response.id },
      fetchPolicy: 'network-only',
      skip: response.order.lastPaymentStatus !== 'REQUIRES_PAYMENT_METHOD',
    },
  );

  useEffect(() => {
    if (response.order.lastPaymentStatus === 'REQUIRES_PAYMENT_METHOD') {
      setLoading(true);
      startPolling(POLLING_INTERVAL);
    } else {
      stopPolling();
      setLoading(false);
    }
    const error = buildErrorMessageIfAny(connectedPaymentTerminal, response.order.firstPaymentTransaction);
    setErrorMessage(error);

    return () => stopPolling();
  }, [connectedPaymentTerminal, response, setLoading, startPolling, stopPolling, setErrorMessage]);

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage(null);

    try {
      await processTerminalPayment();
    } catch (error) {
      setErrorMessage({ reason: (error as PaymentErrorType).message });
      setLoading(false);
    }
  };

  const processTerminalPayment = async () => {
    await onSubmit({
      response,
      paymentMethodSource: PaymentMethodSource.PAYMENT_TERMINAL,
      paymentMethodType: PaymentMethodType.CARD,
      sessionKey: `${response.id}-payment-terminal`,
    });
  };

  const isSubmitDisabled = loading || !canAcceptInPersonPayments || (!!pageId && response.order.totalCents === 0);

  return (
    <div className="terminal-payment-container">
      {connectedPaymentTerminal ? (
        <>
          {!loading && errorMessage && (
            <PaymentError
              title={errorMessage.title ?? paymentError?.title}
              errorMessage={errorMessage.reason}
              customIcon={errorMessage.icon}
              type={errorMessage.type}
            />
          )}
          <form
            onSubmit={event => void handleSubmit(event)}
            className="payment-method"
            aria-label="payment terminal form">
            <button type="submit" disabled={isSubmitDisabled}>
              {loading ? (
                <span className="row align-center justify-center loading">
                  {ICONS['spinner_circle']} Continue on reader
                </span>
              ) : (
                'Charge with card reader'
              )}
            </button>
          </form>
        </>
      ) : (
        <PaymentError
          type="warning"
          title="Accept card payments"
          errorMessage="Connect a reader to accept card payments"
        />
      )}
    </div>
  );
};

export default TerminalPayment;

const buildErrorMessageIfAny = (
  reader: Terminal | undefined | null,
  transaction: Partial<Transaction> | undefined | null,
): TerminalErrorType => {
  if (!transaction || transaction.status !== 'FAILED') return null;

  // all possible errors are here: https://docs.finix.com/additional-resources/developers/implementation-and-testing/failure-codes#payment-failure-codes
  // unmapped errors are shown like "[code] message"

  const makeSureReaderIsOn = 'Make sure it is powered on, connected to the internet and that the Payment App is open.';
  const retryMessage = 'Press "Charge with card reader" to retry.';
  const readerName = reader?.name ?? 'The reader';

  switch (transaction.statusCode) {
    case 'DEVICE_UNREACHABLE':
      return {
        title: 'Unable to connect',
        reason: `${readerName} is unreachable. ${makeSureReaderIsOn}`,
        icon: 'connectionError',
      };

    case 'CANCELLATION_VIA_DEVICE':
      return {
        title: 'Charge canceled',
        reason: retryMessage,
        type: 'warning',
      };

    case 'DEVICE_IN_USE':
      return {
        title: 'The reader is busy',
        reason: `${readerName} is currently waiting for a payment. ${retryMessage}`,
        type: 'warning',
      };

    case 'DEVICE_NOT_CONNECTED':
      return {
        title: 'Unable to connect',
        reason: `${readerName} is offline. ${makeSureReaderIsOn}`,
        icon: 'connectionError',
      };

    case 'EXCEEDED_CARDHOLDER_INPUT_TIME_LIMIT':
      return {
        title: 'Charge canceled',
        reason: `The cardholder did not swipe, tap, or dip the card. ${retryMessage}`,
        type: 'warning',
      };

    case 'COMMUNICATION_ERROR':
      return {
        title: 'Communication error',
        reason: `It was not possible to connect with the payment provider.  ${retryMessage}`,
        icon: 'connectionError',
      };

    default:
      return {
        reason: transaction.statusCode
          ? `[${transaction.statusCode}] ${transaction.statusMessage}`
          : (transaction.statusMessage ?? `Payment failed. ${retryMessage}`),
      };
  }
};
